/**
 * VCardTracking.jsx — Admin : générateur de liens prospects + stats de scan
 *
 * Permet de :
 *   - Créer un lien unique /b/<slug> pour chaque prospect
 *   - Voir le nombre de visites et de scans par prospect
 *   - Détailler chaque événement (action, timestamp, IP, user-agent)
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  BadgePlus, Copy, ExternalLink, Trash2, Loader2, Eye, QrCode,
  Calendar, FileText, Users, Phone as PhoneIcon, Mail, Share2,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const ACTION_LABEL = {
  visit:     { label: 'Carte ouverte',       Icon: Eye,          color: 'text-slate-500' },
  reserve:   { label: 'Réservation Cal.com', Icon: Calendar,     color: 'text-orange-500' },
  plaquette: { label: 'Plaquette',           Icon: FileText,     color: 'text-blue-500' },
  platform:  { label: 'Plateforme B2B',      Icon: Users,        color: 'text-emerald-500' },
  call:      { label: 'Appel',               Icon: PhoneIcon,    color: 'text-orange-500' },
  email:     { label: 'Email',               Icon: Mail,         color: 'text-slate-500' },
  partner:   { label: 'Partenariat',         Icon: Share2,       color: 'text-orange-500' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function AdminVCardTracking() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ prospect_name: '', company: '', email: '', phone: '', note: '' });
  const [creating, setCreating] = useState(false);

  // Detail modal
  const [detail, setDetail] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.vcardList();
      setItems(d.items || []);
    } catch (e) {
      toast.error(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const resetForm = () => setForm({ prospect_name: '', company: '', email: '', phone: '', note: '' });

  const handleCreate = async () => {
    if (!form.prospect_name.trim()) return;
    setCreating(true);
    try {
      const res = await api.vcardCreate(form);
      toast.success(`Lien créé : /b/${res.slug}`);
      resetForm();
      setCreateOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Création impossible');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (slug, short = true) => {
    const url = `${window.location.origin}${short ? '/b' : '/vcard'}/${slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Lien copié'));
  };

  const openCard = (slug) => {
    window.open(`${window.location.origin}/b/${slug}`, '_blank', 'noopener');
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Supprimer définitivement "${slug}" ?`)) return;
    try {
      await api.vcardDelete(slug);
      toast.success('Prospect supprimé');
      await loadAll();
    } catch (e) {
      toast.error(e.message || 'Suppression impossible');
    }
  };

  const openDetail = async (slug) => {
    try {
      const d = await api.vcardGetAdmin(slug);
      setDetail(d);
    } catch (e) {
      toast.error(e.message || 'Impossible de charger les détails');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="admin-vcard-tracking">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold" style={{fontFamily:'Fraunces, serif'}}>
            Carte de visite — Tracking prospects
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Générez un lien unique par prospect — chaque scan de QR est tracé (visite + action + IP)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open('/vcard', '_blank')} data-testid="btn-preview-vcard">
            <Eye className="w-4 h-4 mr-2" /> Aperçu carte générique
          </Button>
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="btn-new-prospect">
            <BadgePlus className="w-4 h-4 mr-2" /> Nouveau prospect
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Aucun prospect. Cliquez sur « Nouveau prospect » pour créer votre premier lien de carte.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Prospect</th>
                    <th className="text-left px-3 py-2 font-semibold">Lien court</th>
                    <th className="text-right px-3 py-2 font-semibold">Visites</th>
                    <th className="text-right px-3 py-2 font-semibold">Scans QR</th>
                    <th className="text-left px-3 py-2 font-semibold">Dernier scan</th>
                    <th className="text-right px-3 py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.id} className="border-t hover:bg-slate-50/50" data-testid={`vcard-row-${p.slug}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.prospect_name}</div>
                        {p.company && <div className="text-xs text-slate-500">{p.company}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/b/{p.slug}</code>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold" data-testid={`visits-${p.slug}`}>{p.visit_count || 0}</td>
                      <td className="px-3 py-2 text-right">
                        <Badge className={(p.scan_count || 0) > 0 ? 'bg-orange-500 text-slate-900' : 'bg-slate-200 text-slate-600'} data-testid={`scans-${p.slug}`}>
                          {p.scan_count || 0}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(p.last_scan_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(p.slug)} title="Détails" data-testid={`btn-detail-${p.slug}`}>
                            <QrCode className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => copyLink(p.slug, true)} title="Copier lien court" data-testid={`btn-copy-${p.slug}`}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openCard(p.slug)} title="Ouvrir carte">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(p.slug)} title="Supprimer" data-testid={`btn-del-${p.slug}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Fraunces, serif'}}>Nouveau prospect</DialogTitle>
            <DialogDescription>
              Générez un lien court unique à partager (SMS, email, imprimé sur une carte).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div>
              <Label>Nom du prospect *</Label>
              <Input
                placeholder="Ex : Jean Dupont"
                value={form.prospect_name}
                onChange={(e) => setForm({...form, prospect_name: e.target.value})}
                data-testid="input-prospect-name"
              />
            </div>
            <div>
              <Label>Entreprise / Auto-école</Label>
              <Input
                placeholder="Ex : Auto-école ABC"
                value={form.company}
                onChange={(e) => setForm({...form, company: e.target.value})}
              />
            </div>
            <div>
              <Label>Email (optionnel)</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
              />
            </div>
            <div>
              <Label>Note interne (optionnel)</Label>
              <Input
                placeholder="Ex : Rencontré au salon Autosur 2026"
                value={form.note}
                onChange={(e) => setForm({...form, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={!form.prospect_name.trim() || creating} data-testid="btn-create-prospect">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BadgePlus className="w-4 h-4 mr-2" />}
              Créer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DETAIL DIALOG */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle style={{fontFamily:'Fraunces, serif'}}>
                  {detail.prospect_name}
                  {detail.company && <span className="text-sm text-slate-500 font-normal"> · {detail.company}</span>}
                </DialogTitle>
                <DialogDescription>
                  Lien court : <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/b/{detail.slug}</code>
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-3 gap-3 bg-slate-900 text-white rounded-lg p-3">
                <div>
                  <div className="text-xs text-slate-400">Visites</div>
                  <div className="text-xl font-bold">{detail.visit_count || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-orange-400">Scans QR</div>
                  <div className="text-xl font-bold text-orange-400">{detail.scan_count || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400">Créé le</div>
                  <div className="text-xs mt-1">{fmtDate(detail.created_at)}</div>
                </div>
              </div>

              {/* QR code pour partage rapide */}
              <div className="flex items-center gap-4 bg-slate-50 rounded-lg p-3">
                <div className="bg-white rounded p-2 border">
                  <QRCodeSVG value={`${window.location.origin}/b/${detail.slug}`} size={100} />
                </div>
                <div className="flex-1 text-xs">
                  <div className="text-slate-500 mb-1">URL à partager / imprimer</div>
                  <div className="font-mono text-[11px] break-all mb-2">{`${window.location.origin}/b/${detail.slug}`}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyLink(detail.slug, true)}>Copier</Button>
                    <Button size="sm" variant="outline" onClick={() => openCard(detail.slug)}>Ouvrir</Button>
                  </div>
                </div>
              </div>

              {/* Historique scans */}
              <div>
                <div className="text-sm font-semibold mb-2">Historique des événements ({detail.scans?.length || 0})</div>
                {(detail.scans || []).length === 0 ? (
                  <div className="text-xs text-slate-500 italic bg-slate-50 rounded p-3 text-center">
                    Aucun scan enregistré pour l'instant. Partagez le lien pour commencer à tracker.
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border rounded">
                    {(detail.scans || []).slice().reverse().map((s, i) => {
                      const meta = ACTION_LABEL[s.action] || { label: s.action, Icon: QrCode, color: 'text-slate-500' };
                      const Icon = meta.Icon;
                      return (
                        <div key={i} className="flex items-center gap-2 border-b last:border-0 px-3 py-2 text-xs hover:bg-slate-50">
                          <Icon className={`w-4 h-4 ${meta.color} shrink-0`} />
                          <div className="flex-1">
                            <div className="font-medium">{meta.label}</div>
                            <div className="text-slate-500">{fmtDate(s.at)} · IP {s.ip || '?'}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Fermer</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
